import { useCallback } from 'react';
import { useDispatch } from '_common/hooks';
import { FormattedMessage, useIntl } from 'react-intl';
import { Divider, Button, Toggle, IconName, Tooltip } from '@ets/design-system';
import { openAndUpdateModal } from '_common/modals/ModalsSlice';
import { ShortcutsPopover } from '_common/components';
import styles from './ImageOptionsWidget.module.scss';

type ImageOptionsWidgetProps = {
  currentProperty: Editor.Edition.ImageWrapProperty;
  offsets: Rect | null;
  onImageWrapChange: (props: Editor.Edition.ImageProperties) => void;
};

const ImageOptionsWidgetView = ({
  currentProperty,
  offsets,
  onImageWrapChange,
}: ImageOptionsWidgetProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const PROPERTIES: {
    id: Editor.Edition.ImageWrapProperty;
    icon: IconName;
    label: string;
  }[] = [
    {
      id: 'inline',
      icon: 'wrap-in-line-with-text',
      label: intl.formatMessage({ id: 'IN_LINE_WITH_TEXT' }),
    },
    { id: 'left', icon: 'wrap-left', label: intl.formatMessage({ id: 'LEFT' }) },
    { id: 'right', icon: 'wrap-right', label: intl.formatMessage({ id: 'RIGHT' }) },
    {
      id: 'topAndBottom',
      icon: 'wrap-top-and-bottom',
      label: intl.formatMessage({ id: 'TOP_AND_BOTTOM' }),
    },
    {
      id: 'behindText',
      icon: 'wrap-behind-text',
      label: intl.formatMessage({ id: 'BEHIND_TEXT' }),
    },
    {
      id: 'inFrontText',
      icon: 'wrap-front-text',
      label: intl.formatMessage({ id: 'IN_FRONT_OF_TEXT' }),
    },
  ];

  const setCurrentProperty = useCallback(
    (property: Editor.Edition.ImageWrapProperty) => {
      if (property !== currentProperty) {
        /*TODO:InlineProperties callback*/
        onImageWrapChange({
          imageWrap: {
            value: property,
          },
        });
      }
    },
    [currentProperty],
  );

  const handleInsertCaption = () => {
    dispatch(openAndUpdateModal({ modal: 'CaptionsModal', data: { editModal: 'insert' } }));
  };

  if (!offsets) {
    return null;
  }

  return (
    <ShortcutsPopover testId="image" offsets={offsets}>
      <div className={styles.root}>
        <div className={styles.properties}>
          {PROPERTIES.map((property) => (
            <Tooltip
              key={property.id}
              content={property.label}
              testId={`image-property-${property.id}`}
            >
              <span>
                <Toggle
                  size="l"
                  variant="link"
                  iconPrefixActive="fak"
                  iconPrefixInactive="fak"
                  iconProps={{
                    icon: property.icon,
                    color: currentProperty === property.id ? 'primary' : 'neutral',
                  }}
                  isToggled={currentProperty === property.id}
                  onClick={() => {
                    setCurrentProperty(property.id);
                  }}
                  testId={`image-property-${property.id}`}
                  style={{ padding: 0 }}
                />
              </span>
            </Tooltip>
          ))}
        </div>
        <div style={{ height: '3rem' }}>
          <Divider vertical margin="0 1rem" />
        </div>
        <Button size="m" variant="link" onClick={handleInsertCaption} testId="image-insert-caption">
          <FormattedMessage id="INSERT_CAPTION" />
        </Button>
      </div>
    </ShortcutsPopover>
  );
};

export default ImageOptionsWidgetView;
